import type { Translate } from "@khazzin/i18n";
import { Info } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import { EmptyState } from "../patterns/page/states";
import { IconButton } from "../primitives/Button";
import { Tooltip } from "../primitives/Display";
import { SegmentedControl } from "../primitives/SegmentedControl";
import { cx } from "../styles";
import { type ChartData, NARROW_QUERY, useMediaQuery, valueText, xText } from "./data";

/**
 * The frame every chart sits in (EP13-S04, `Analytics.dc.html#exec`): a title,
 * the metric's definition behind an info button, a **Chart / Table** toggle and
 * a slot for the export action.
 *
 * **Every chart can be read as a table**, built from the same rows the chart
 * draws (`13/01` §7). At 360 px and under only the table renders and the toggle
 * goes away: a chart that narrow has no room for its axis labels.
 *
 * The chart is one `role="img"` with a summary sentence for its name; the
 * table is the accessible reading of the figures, and switching to it is
 * announced without moving focus off the toggle.
 */

export type ChartView = "chart" | "table";

export interface ChartCardProps {
	title: string;
	/** The metric's definition (`useMetricDefinition`), shown behind the info button. */
	definition?: string;
	/** One sentence naming what the chart shows, for the `role="img"` label. */
	summary: string;
	data: ChartData;
	t: Translate;
	/** The export action, or anything else that belongs beside the toggle. */
	actions?: ReactNode;
	/** The chart itself: one of the kit's charts over the same `data`. */
	children: ReactNode;
	className?: string;
}

export function ChartCard({
	title,
	definition,
	summary,
	data,
	t,
	actions,
	children,
	className,
}: ChartCardProps) {
	const [view, setView] = useState<ChartView>("chart");
	const narrow = useMediaQuery(NARROW_QUERY);
	const showTable = narrow || view === "table";
	const titleId = useId();

	return (
		<section
			aria-labelledby={titleId}
			className={cx("flex min-w-0 flex-col gap-3 card p-4", className)}
		>
			<header className="flex flex-wrap items-center gap-2">
				<h3 id={titleId} className="min-w-0 flex-1 truncate font-bold text-[13px] text-fg">
					{title}
				</h3>
				{definition !== undefined && (
					<Tooltip content={definition}>
						<IconButton icon={Info} label={definition} size="sm" variant="ghost" />
					</Tooltip>
				)}
				{!narrow && (
					<SegmentedControl<ChartView>
						aria-label={t("ui.chart.view")}
						options={[
							{ value: "chart", label: t("ui.chart.chart") },
							{ value: "table", label: t("ui.chart.table") },
						]}
						value={view}
						onChange={setView}
					/>
				)}
				{actions}
			</header>

			{/* Says which reading is showing when the toggle changes it, so a screen
			    reader hears the table arrive while focus stays on the toggle. */}
			<p className="sr-only" aria-live="polite">
				{showTable ? t("ui.chart.showingTable", { title }) : ""}
			</p>

			{data.rows.length === 0 ? (
				// No rows is no chart: an axis frame with nothing in it reads as a
				// rendering fault, and a table of no rows as a broken filter.
				<EmptyState
					illustration="none"
					title={t("ui.chart.empty.title")}
					body={t("ui.chart.empty.body")}
				/>
			) : showTable ? (
				<ChartTable data={data} caption={title} />
			) : (
				<div role="img" aria-label={summary} className="min-w-0">
					{children}
				</div>
			)}
		</section>
	);
}

/**
 * The chart's rows as a table: the category down the side, one column per
 * series, every figure written out. It mirrors in Arabic with the document.
 */
export function ChartTable({ data, caption }: { data: ChartData; caption: string }) {
	return (
		<div className="max-h-80 overflow-auto">
			<table className="w-full border-collapse text-[12.5px]">
				<caption className="sr-only">{caption}</caption>
				<thead>
					<tr className="border-border border-b">
						<th scope="col" className="py-1.5 pe-3 text-start font-semibold text-fg-subtle">
							{data.x.label}
						</th>
						{data.series.map((series) => (
							<th
								key={series.key}
								scope="col"
								className="py-1.5 ps-3 text-end font-semibold text-fg-subtle"
							>
								{series.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{data.rows.map((row, index) => (
						// The category is not guaranteed unique (a histogram's open bucket),
						// so the position keys the row.
						// biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity beyond their order
						<tr key={index} className="border-border border-b last:border-b-0">
							<th scope="row" className="py-1.5 pe-3 text-start font-medium text-fg">
								{xText(data, row)}
							</th>
							{data.series.map((series) => (
								<td
									key={series.key}
									className="py-1.5 ps-3 text-end text-fg [font-variant-numeric:tabular-nums]"
								>
									{valueText(data, row[series.key])}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
